import {Component, OnInit} from '@angular/core';
import {StepComponent} from "../../step/step.component";
import {Step, StepService} from "../../services/step.service";
import {Router} from "@angular/router";
import {ModeratRequestService} from "../services/request.service";
import {ModeratAnfrage} from "../anfrage/moderat.anfrage";
import {ElementService} from "../../services/element.service";
import {PortalService} from "../../services/portal.service";
import {VerbrauchsanalyseModeratForm} from "./verbrauchsanalyse.moderat.form";
import {StepNavigationElement} from "../../step/step.navigation.element";
import {WpcheckStartComponent} from "../wpcheck/wpcheck.start.component";
import {EnergyMapService} from "../../energy-map/services/energy.map.service";
import {HeizcheckConstants} from "../../heizcheck/heizcheck.constants";

/**
 * Overwrite the Generic step component for the first section fo the moderat.
 *
 * @inheritDoc
 */
@Component({
    selector: 'app-step-verbrauchsanalyse',
    templateUrl: '../../step/step.component.html',
    standalone: false
})
export class VerbrauchsanalyseStepComponent extends StepComponent implements OnInit{

  /**
   * (input) data of the user
   */
  anfrage: ModeratAnfrage;

  /**
   * @inheritDoc
   */
  formConfig: VerbrauchsanalyseModeratForm;

  /**
   * Overwrite the label prefix.
   */
  textkeyPrefix: string = "moderat";

  /**
   * @inheritDoc
   * @param stepService
   * @param router
   * @param requestService
   * @param elementService
   * @param portalService
   */
  constructor(protected stepService: StepService, protected router: Router, protected requestService: ModeratRequestService, protected elementService: ElementService,
              protected portalService: PortalService, protected energyMapService: EnergyMapService) {
    super(stepService, router, elementService);
  }

  /**
   * @inheritDoc
   */
  ngOnInit() {
    // add the correct form config that we need in this steps and init the parent form
    this.formConfig = new VerbrauchsanalyseModeratForm(this.energyMapService);

    // formConfig.anfrage has to be set before super.ngOnInit() (builds the form via getForm(),
    // which reads 'groups'); `this.anfrage` is assigned later, see comment below.
    const anfrage = this.requestService.getAnfrage();
    // check if showing the step address is set in the portal config and energy source is not 'fernwaerme', then save it to anfrage object
    const configAddress = this.portalService.getConfig('fernwaerme.enable.step.address');
    anfrage.showStepAddress = !!configAddress && anfrage.verbrauchsanalyseInput?.energietraeger_name !== 'fernwaerme';
    this.formConfig.anfrage = anfrage;
    // select the 'basisinformationen' groups mapping (PLZ/Adresse first for interesse === 'fernwaerme', unchanged order otherwise)
    this.formConfig.updateGroups();

    super.ngOnInit();

    // now safe to expose it on the component too: getForm() has already run, so formConfig.form exists
    this.anfrage = anfrage;
    this.formConfig.setData(this.anfrage.verbrauchsanalyseInput);

    // update the previous step
    this.onStepUpdate();

    // show navigation element back to the wpcheck
    if(this.anfrage.mode === ModeratAnfrage.MODE_WPCHECK) {
      this.stepService.setNavigationPreElement(
        new StepNavigationElement('moderat.nav.wpcheck.', true, WpcheckStartComponent.wpcheckUrl.replace('{{portalId}}', this.portalService.currentPortalId))
      );
    }

    // init error placeholder
    this.initErrorPlaceholder();
  }

  /**
   * @inheritDoc
   * @param data
   */
  saveData(data:{[key: string]: any}) {
    // if the confirmed address actually changed (e.g. the user went back and entered a
    // different address), any previously determined district heating status is stale and
    // must be cleared so it gets recomputed wherever it's needed next (fernwaerme/ergebnis
    // step, or SanierungComponent's own check for the "address step only" portal case)
    const addressFields = ['adresse_postalcode', 'adresse_strasse', 'adresse_hausnummer'];
    const addressChanged = addressFields.some(field =>
      data.hasOwnProperty(field) && data[field] !== this.anfrage.verbrauchsanalyseInput?.[field]
    );

    // merge the data with the existing data
    this.anfrage.verbrauchsanalyseInput = {
      ...this.anfrage.verbrauchsanalyseInput,
      ...data,
      ...(addressChanged ? {fernwaermenetz: null} : {}),
    };
    this.requestService.setAnfrage(this.anfrage);

    // save the data also to the server
    this.requestService.saveDataToServer();

    super.saveData(data);
  }

  /**
   * @inheritDoc
   */
  onStepUpdate(): void {
    super.onStepUpdate();

    // help texts are translated per default step number; resolve the key via the field shown
    // here instead of the route number, since that shifts for interesse === 'fernwaerme'
    if(this.anfrage?.interesse === 'fernwaerme' && this.steps?.currentStep?.name?.startsWith('basisinformationen/')) {
      const fieldId = this.formConfig.getFieldnamesOfGroup(this.steps.currentStep.name)[0];
      const defaultGroupName = fieldId ? this.formConfig.getDefaultGroupName(fieldId) : null;
      if(defaultGroupName) {
        this.helpTextkey = this.textkeyPrefix + '.help.' + defaultGroupName.replace('/', '.');
      }
    }

    // in the district heating flow the info page 'start' is skipped, so the back button has to skip it as well
    if(this.anfrage?.interesse === 'fernwaerme' && this.previousStep?.name === 'start') {
      const entryPage = this.anfrage?.extraFields && Object.keys(this.anfrage.extraFields).length > 0 ? 'extern' : 'einstieg';
      this.previousStep = new Step(entryPage, '/' + this.portalService.currentPortalId + '/moderat/' + entryPage);
    }
  }

  /**
   * Return the next step from a step list
   * @param stepList
   */
  getNextStepFromList(stepList: Step[]): any {
    // set all data again in the form to avoid using old data for toggle fields
    if(this.anfrage?.verbrauchsanalyseInput && this.formConfig) {
      this.formConfig.updateFields(this.anfrage.verbrauchsanalyseInput);
      // save the data again. In some case some fields are autoupdatetd
      this.saveData({});
    }

    // the district heating result page is only part of the flow if the user is interested in district heating
    if(this.anfrage?.interesse !== 'fernwaerme') {
      stepList = stepList?.filter(step => step.name !== 'fernwaerme/ergebnis');
    }

    return super.getNextStepFromList(stepList);
  }

  /**
   * @inheritDoc
   */
  onSubmit(): void {
    /* some inputs end the advisor */
    if(this.isAdvisorEnd()) {
      // save data
      this.saveData(this.formConfig.getData());

      // navigate to end page
      this.router.navigateByUrl(this.portalService.currentPortalId + "/moderat/verbrauchsanalyse/ausstieg", {state: {lastStep: this.router.url}});
      return;
    }

    super.onSubmit();
  }

  /**
   * In some cases the advisor has to stop because we cannot offer any advice for this user/building
   * Hint: new cases have to be added also to VerbrauchsanalyseAusstiegComponent
   */
  isAdvisorEnd(): boolean {
    // not central heating
    if(this.form.get("heizungstyp") && (this.form.get("heizungstyp")?.value === "dezentral" || this.form.get("heizungstyp")?.value === "dezentral2")) {
      return true;
    }

    // mieter
    if(this.form.get("nutzungstyp") && this.form.get("nutzungstyp")?.value === "mieter") {
      return true;
    }

    // denkmalschuz
    if(this.form.get("gebaeude_denkmalschutz") && this.form.get("gebaeude_denkmalschutz")?.value === "ja") {
      return true;
    }

    return false;
  }

  /**
   * inits placeholder for error messages
   */
  initErrorPlaceholder(): void {
    if(this.form?.controls['anzahl_wohneinheiten']) {
      this.subscriptions.push(this.form?.controls['anzahl_wohneinheiten']?.valueChanges?.subscribe((value) => {
        const averageSize = Math.round(this.anfrage.verbrauchsanalyseInput.gebaeude_flaeche / value);
        if (averageSize < HeizcheckConstants.APARTMENT_SIZE_MIN || averageSize > HeizcheckConstants.APARTMENT_SIZE_MAX) {
          this.stepErrorPlaceholder = {
            apartmentSize: averageSize,
            portalId: this.portalService?.currentPortalId
          }
        }
      }));
    }
  }

}
